/**
 * @file App.tsx
 * @description Main Waiter Floor Service Terminal for TableGreet.
 * 
 * ARCHITECTURAL RESPONSIBILITIES:
 * 1. Primary navigation: Orders, Calls, and Reservations.
 * 2. Orders pipeline filters: All, New, Preparing, Ready, and Done.
 * 3. Order card: Table num, ID, Department, Date/Time, Price, Payment method,
 *    Service pipeline status with allergens, client order details, and "Confirm to Kitchen" action.
 * 4. Mounts TableWorkspaceModal for on-the-fly order entry and item additions.
 * 5. Mounts personal shift performance tracking with owner permission controls.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { WaiterTopBar } from './shared/components/WaiterTopBar';
import { WaiterOrderCard } from './features/orders/components/WaiterOrderCard';
import { CallsListView } from './features/notifications/components/CallsListView';
import { ReservationsListView } from './features/reservations/components/ReservationsListView';
import { TableWorkspaceModal } from './features/tablescreen/components/TableWorkspaceModal';
import { WaiterNotificationsModal } from './features/notifications/components/WaiterNotificationsModal';
import { WaiterStatsModal } from './features/stats/components/WaiterStatsModal';
import { WaiterLoginModal } from './features/auth/components/WaiterLoginModal';
import { useWaiterAuth } from './features/auth/hooks/useWaiterAuth';
import { useWaiterOrders } from './features/orders/hooks/useWaiterOrders';
import { VoidOrderModal } from './features/tableactions/components/VoidOrderModal';
import { useWaiterNotifications } from './features/notifications/hooks/useWaiterNotifications';
import { useWaiterReservations } from './features/reservations/hooks/useWaiterReservations';
import type { WaiterActiveOrder, OrderPipelineStatus } from './types/waiterOrder.types';
import type { WaiterTable } from './types/waiter.types';
import {
  RefreshCw,
  Plus,
  Search,
  UtensilsCrossed,
  X,
  ChefHat,
  Flame,
  CheckCircle2,
} from 'lucide-react';
import logo from './assets/logo.svg';

export const App: React.FC = () => {
  const { sessionUser, activeVenue, waiterName, login, logout, isLoading: isAuthLoading } = useWaiterAuth();
  
  // 1. Orders hook
  const {
    orders,
    isLoading: isOrdersLoading,
    error: ordersError,
    refreshOrders,
    handleConfirmToKitchen,
    handleServeOrder,
    handleCollectPayment,
    handleResolveGuestCall,
    handleCancelOrder,
  } = useWaiterOrders(activeVenue?.id || null);

  // 2. Calls hook
  const {
    calls,
    resolveCall,
  } = useWaiterNotifications(activeVenue?.id || null);

  // 3. Reservations hook
  const {
    reservations,
    upcomingCount,
    isLoading: isReservationsLoading,
    handleUpdateStatus: handleUpdateReservationStatus,
  } = useWaiterReservations(activeVenue?.id || null);

  // Navigation & UI state
  const [activeNavTab, setActiveNavTab] = useState<'orders' | 'calls' | 'reservations'>('orders');
  const [orderFilter, setOrderFilter] = useState<'all' | OrderPipelineStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTable, setActiveTable] = useState<WaiterTable | null>(null);
  const [isNotifsOpen, setIsNotifsOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [isQuickTableOpen, setIsQuickTableOpen] = useState(false);
  const [quickTableInput, setQuickTableInput] = useState('');
  const [quickGuestCount, setQuickGuestCount] = useState(2);
  const [voidCandidateOrder, setVoidCandidateOrder] = useState<WaiterActiveOrder | null>(null);

  // Operational metrics for orders computed in a single O(N) pass
  const { allCount, newCount, preparingCount, readyCount, doneCount } = useMemo(() => {
    let n = 0;
    let p = 0;
    let r = 0;
    let d = 0;
    for (let i = 0; i < orders.length; i++) {
      const status = orders[i].pipeline_status;
      if (status === 'new') n++;
      else if (status === 'preparing') p++;
      else if (status === 'ready') r++;
      else if (status === 'done') d++;
    }
    return {
      allCount: orders.length,
      newCount: n,
      preparingCount: p,
      readyCount: r,
      doneCount: d,
    };
  }, [orders]);

  // Filter orders by search query and pipeline status, sorted by floor urgency:
  // 1. Ready on pass (hot food waiting to be served)
  // 2. Active guest calls (bills, water, assistance)
  // 3. New unconfirmed orders (awaiting waiter confirmation to kitchen)
  // 4. Preparing orders
  // 5. Done / completed orders at the end
  const displayedOrders = useMemo(() => {
    const list = orders.filter(order => {
      // 1. Status filter
      if (orderFilter !== 'all' && order.pipeline_status !== orderFilter) {
        return false;
      }

      // 2. Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesTable = order.table_number.toLowerCase().includes(q);
        const matchesOrder = order.order_number.toLowerCase().includes(q);
        const matchesDept = order.department?.toLowerCase().includes(q) || false;
        const matchesGuest = order.guest_name?.toLowerCase().includes(q) || false;
        const matchesItems = order.items.some(i => i.name.toLowerCase().includes(q));
        if (!matchesTable && !matchesOrder && !matchesDept && !matchesGuest && !matchesItems) {
          return false;
        }
      }

      return true;
    });

    return list.sort((a, b) => {
      // Priority 1: Hot food ready at pass
      const aIsReady = a.pipeline_status === 'ready';
      const bIsReady = b.pipeline_status === 'ready';
      if (aIsReady && !bIsReady) return -1;
      if (!aIsReady && bIsReady) return 1;

      // Priority 2: Guest calls or bill requests
      const aHasCall = Boolean(a.guest_action && a.guest_action !== 'idle');
      const bHasCall = Boolean(b.guest_action && b.guest_action !== 'idle');
      if (aHasCall && !bHasCall) return -1;
      if (!aHasCall && bHasCall) return 1;

      // Priority 3: New orders needing confirmation to kitchen
      const aIsNewPending = a.pipeline_status === 'new' && !a.is_confirmed_to_kitchen;
      const bIsNewPending = b.pipeline_status === 'new' && !b.is_confirmed_to_kitchen;
      if (aIsNewPending && !bIsNewPending) return -1;
      if (!aIsNewPending && bIsNewPending) return 1;

      // Priority 4: Done orders pushed to bottom
      const aIsDone = a.pipeline_status === 'done';
      const bIsDone = b.pipeline_status === 'done';
      if (aIsDone && !bIsDone) return 1;
      if (!aIsDone && bIsDone) return -1;

      // Chronological fallback
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [orders, orderFilter, searchQuery]);

  // Convert order to WaiterTable for workspace ordering modal
  const handleOpenWorkspace = useCallback((order: WaiterActiveOrder) => {
    const cleanNum = order.table_number.replace(/^table\s*/i, '').trim();
    const tableNode: WaiterTable = {
      id: order.table_id || `table-${cleanNum}`,
      table_number: cleanNum,
      section: order.section,
      seats: order.guest_count || 2,
      shape: 'square',
      status: order.pipeline_status === 'ready' ? 'ready' : order.guest_action === 'bill_requested' ? 'needs_payment' : 'preparing',
      active_order_id: order.id,
      active_order_number: order.order_number,
      guest_count: order.guest_count,
      subtotal: order.total_amount,
      waiter_id: order.waiter_id,
      waiter_name: order.waiter_name,
      currency: order.currency,
    };
    setActiveTable(tableNode);
  }, []);

  const handleVoidCandidateOrder = useCallback((order: WaiterActiveOrder) => {
    setVoidCandidateOrder(order);
  }, []);

  // Opens order workspace for a table, creating a table session if one does not exist
  const handleTakeOrderForTable = (tableNum: string, guestCount = 2) => {
    const cleanNum = tableNum.trim();
    if (!cleanNum) return;

    const existingOrder = orders.find(
      o => o.table_number.toLowerCase().includes(cleanNum.toLowerCase())
    );

    if (existingOrder) {
      handleOpenWorkspace(existingOrder);
    } else {
      const newTableNode: WaiterTable = {
        id: `custom-table-${cleanNum}`,
        table_number: cleanNum,
        section: 'Main Dining',
        seats: guestCount || 2,
        shape: 'square',
        status: 'ordering',
        guest_count: guestCount || 2,
        subtotal: 0,
        currency: 'AED',
      };
      setActiveTable(newTableNode);
    }
  };

  const handleOpenQuickTable = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanNum = quickTableInput.trim();
    if (!cleanNum) return;

    handleTakeOrderForTable(cleanNum, quickGuestCount);
    setQuickTableInput('');
    setIsQuickTableOpen(false);
  };

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-zinc-400">
        <img src={logo} className="w-12 h-12 object-contain animate-pulse mb-3" alt="TableGreet" />
        <p className="font-mono text-xs text-zinc-400 tracking-wider">INITIALIZING TABLEGREET FLOOR TERMINAL...</p>
      </div>
    );
  }

  if (!sessionUser) {
    return <WaiterLoginModal onLogin={login} />;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col overflow-hidden">
      {/* Top Header with Navigation: Orders, Calls, Reservations */}
      <WaiterTopBar
        venueName={activeVenue?.name || 'TableGreet Floor'}
        waiterName={waiterName}
        activeNavTab={activeNavTab}
        onChangeNavTab={setActiveNavTab}
        ordersCount={allCount}
        pendingCallsCount={calls.length}
        reservationsCount={upcomingCount}
        readyOrdersCount={readyCount}
        onOpenNotifications={() => setIsNotifsOpen(true)}
        onOpenStats={() => setIsStatsOpen(true)}
        onLogout={logout}
      />

      {/* Main Content Area */}
      <main className="flex-1 p-4 overflow-y-auto">
        {/* 1. ORDERS VIEW */}
        {activeNavTab === 'orders' && (
          <div className="space-y-4">
            {/* Filter Toolbar: All, New, Preparing, Ready, Done */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-[#121212] p-3 rounded-2xl border border-white/10">
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
                {/* All */}
                <button
                  type="button"
                  onClick={() => setOrderFilter('all')}
                  className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
                    orderFilter === 'all'
                      ? 'bg-white/15 text-white border border-white/20 font-black'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <span>all</span>
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-white/10 text-zinc-300">
                    {allCount}
                  </span>
                </button>

                {/* New */}
                <button
                  type="button"
                  onClick={() => setOrderFilter('new')}
                  className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
                    orderFilter === 'new'
                      ? 'bg-[#ff4f00] text-white shadow-lg shadow-[#ff4f00]/30 font-black'
                      : newCount > 0
                      ? 'text-[#ff4f00] hover:text-orange-400'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <ChefHat className="w-3.5 h-3.5" />
                  <span>new</span>
                  {newCount > 0 && (
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-black/40 text-white animate-pulse">
                      {newCount}
                    </span>
                  )}
                </button>

                {/* Preparing */}
                <button
                  type="button"
                  onClick={() => setOrderFilter('preparing')}
                  className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
                    orderFilter === 'preparing'
                      ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30 font-black'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <Flame className="w-3.5 h-3.5" />
                  <span>preparing</span>
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-white/10 text-zinc-300">
                    {preparingCount}
                  </span>
                </button>

                {/* Ready */}
                <button
                  type="button"
                  onClick={() => setOrderFilter('ready')}
                  className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
                    orderFilter === 'ready'
                      ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30 font-black'
                      : readyCount > 0
                      ? 'text-purple-400 hover:text-purple-300'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>ready</span>
                  {readyCount > 0 && (
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-purple-950 text-purple-200 animate-pulse">
                      {readyCount}
                    </span>
                  )}
                </button>

                {/* Done */}
                <button
                  type="button"
                  onClick={() => setOrderFilter('done')}
                  className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
                    orderFilter === 'done'
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30 font-black'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>done</span>
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-white/10 text-zinc-300">
                    {doneCount}
                  </span>
                </button>
              </div>

              {/* Search & New Order Action */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1 md:w-56">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search table, order..."
                    className="w-full min-h-[40px] pl-9 pr-8 py-2 rounded-xl bg-[#0a0a0a] border border-white/10 text-base sm:text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#ff4f00] transition-colors"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="min-w-[32px] min-h-[32px] flex items-center justify-center absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => setIsQuickTableOpen(true)}
                  className="min-h-[40px] px-3.5 py-2 rounded-xl bg-[#ff4f00] hover:bg-[#e64700] active:scale-[0.98] text-white text-xs font-black uppercase flex items-center gap-1.5 shadow-lg shadow-[#ff4f00]/25 transition-all cursor-pointer shrink-0"
                >
                  <Plus className="w-4 h-4" />
                  <span>New Order</span>
                </button>
              </div>
            </div>

            {/* Error Banner */}
            {ordersError && (
              <div className="p-3 rounded-xl bg-rose-950/60 border border-rose-800/80 text-rose-300 text-xs font-semibold flex items-center justify-between">
                <span>{ordersError}</span>
                <button
                  type="button"
                  onClick={refreshOrders}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-rose-900/50 hover:bg-rose-900 text-rose-200 text-xs cursor-pointer"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Retry</span>
                </button>
              </div>
            )}

            {/* Loading / Empty / Orders Grid */}
            {isOrdersLoading && orders.length === 0 ? (
              <div className="h-96 flex flex-col items-center justify-center text-zinc-500">
                <RefreshCw className="w-8 h-8 animate-spin mb-2 text-[#ff4f00]" />
                <p className="text-sm font-medium">Synchronizing orders...</p>
              </div>
            ) : displayedOrders.length === 0 ? (
              <div className="h-96 flex flex-col items-center justify-center text-zinc-500 text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#121212] flex items-center justify-center mb-3 border border-white/10">
                  <UtensilsCrossed className="w-8 h-8 text-zinc-600" />
                </div>
                <p className="text-base font-bold text-zinc-300">No Orders in "{orderFilter}"</p>
                <p className="text-xs text-zinc-500 mt-1 max-w-sm">
                  {searchQuery
                    ? 'No orders match your search query.'
                    : `There are currently no orders in the ${orderFilter} stage.`}
                </p>
                {searchQuery || orderFilter !== 'all' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setOrderFilter('all');
                    }}
                    className="mt-3 px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-xs text-zinc-300 hover:text-white cursor-pointer"
                  >
                    Clear Filter
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsQuickTableOpen(true)}
                    className="mt-4 px-4 py-2 rounded-2xl bg-[#ff4f00] hover:bg-[#e64700] text-white text-xs font-black uppercase tracking-wider flex items-center gap-1.5 shadow-lg shadow-[#ff4f00]/25 cursor-pointer"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Take New Order</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {displayedOrders.map(order => (
                  <WaiterOrderCard
                    key={order.id}
                    order={order}
                    onConfirmToKitchen={handleConfirmToKitchen}
                    onServe={handleServeOrder}
                    onCollectPayment={handleCollectPayment}
                    onResolveCall={handleResolveGuestCall}
                    onOpenWorkspace={handleOpenWorkspace}
                    onVoidOrder={handleVoidCandidateOrder}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* 2. CALLS VIEW */}
        {activeNavTab === 'calls' && (
          <CallsListView
            calls={calls}
            isLoading={false}
            onResolveCall={resolveCall}
          />
        )}

        {/* 3. RESERVATIONS VIEW */}
        {activeNavTab === 'reservations' && (
          <ReservationsListView
            reservations={reservations}
            isLoading={isReservationsLoading}
            onUpdateStatus={handleUpdateReservationStatus}
            onTakeOrder={handleTakeOrderForTable}
          />
        )}
      </main>

      {/* Quick Table / New Order Modal */}
      {isQuickTableOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-[#0a0a0a]/90 backdrop-blur-md animate-fade-in">
          <div className="w-full max-w-sm bg-[#121212] border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 shadow-2xl pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:pb-6">
            {/* Mobile slide handle */}
            <div className="sm:hidden flex justify-center pb-2 -mt-1">
              <div className="w-10 h-1 rounded-full bg-zinc-700/60" />
            </div>

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-black text-white uppercase tracking-tight">
                Open Table Order
              </h3>
              <button
                type="button"
                onClick={() => setIsQuickTableOpen(false)}
                aria-label="Close"
                className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleOpenQuickTable} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                  Table Number or Name
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={quickTableInput}
                  onChange={e => setQuickTableInput(e.target.value)}
                  placeholder="e.g. 12, VIP 1, Patio 3"
                  className="w-full min-h-[46px] px-4 py-3 rounded-2xl bg-[#0a0a0a] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-[#ff4f00] text-base sm:text-sm font-semibold transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                  Guest Count
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 4, 6, 8].map(count => (
                    <button
                      key={count}
                      type="button"
                      onClick={() => setQuickGuestCount(count)}
                      className={`flex-1 min-h-[44px] py-2 rounded-xl text-xs font-bold cursor-pointer transition-all active:scale-[0.98] ${
                        quickGuestCount === count
                          ? 'bg-[#ff4f00] text-white shadow-lg shadow-[#ff4f00]/25'
                          : 'bg-[#0a0a0a] border border-white/10 text-zinc-400 hover:text-white'
                      }`}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsQuickTableOpen(false)}
                  className="flex-1 min-h-[48px] py-3 rounded-2xl bg-white/5 hover:bg-white/10 active:scale-[0.98] text-zinc-400 hover:text-white font-bold text-xs uppercase cursor-pointer transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 min-h-[48px] py-3 rounded-2xl bg-[#ff4f00] hover:bg-[#e64700] active:scale-[0.98] text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-[#ff4f00]/30 cursor-pointer transition-all"
                >
                  Start Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table Workspace Ordering Modal */}
      {activeTable && activeVenue && (
        <TableWorkspaceModal
          isOpen={Boolean(activeTable)}
          onClose={() => setActiveTable(null)}
          table={activeTable}
          allTables={[]}
          venueId={activeVenue.id}
          waiterId={sessionUser.id}
          waiterName={waiterName}
          onTableUpdated={() => {
            refreshOrders();
            setActiveTable(null);
          }}
        />
      )}

      {/* Guest Notifications Modal */}
      <WaiterNotificationsModal
        isOpen={isNotifsOpen}
        onClose={() => setIsNotifsOpen(false)}
        calls={calls}
        readyCount={readyCount}
        onResolveCall={resolveCall}
      />

      {/* Waiter Shift Stats Modal */}
      {activeVenue && (
        <WaiterStatsModal
          isOpen={isStatsOpen}
          onClose={() => setIsStatsOpen(false)}
          venueId={activeVenue.id}
          waiterName={waiterName}
        />
      )}

      {/* Direct Order Void / Cancel Modal */}
      {voidCandidateOrder && (
        <VoidOrderModal
          isOpen={Boolean(voidCandidateOrder)}
          onClose={() => setVoidCandidateOrder(null)}
          orderNumber={voidCandidateOrder.order_number}
          tableNumber={voidCandidateOrder.table_number}
          onConfirmVoid={async reason => {
            await handleCancelOrder(voidCandidateOrder.id, reason);
            setVoidCandidateOrder(null);
          }}
        />
      )}
    </div>
  );
};
